import { ReactNode, useCallback, useEffect, useState } from 'react';
import GlobalStateContext from './context';
import { useGetAccountMetadata } from './metadata';
import { useAuth } from '@bymason-private/auth-provider';
import {
  ControllerVersion,
  FeatureFlag,
  useGetUserPrefs,
  useUpdateUserPrefs,
} from '@bymason/controller-api-hooks';
import { useGetFeatureFlags } from './feature-flags';

/**
 * This is the provider that maintains global state across the entire application. It's important to understand
 * that when any one of the state items exposed below is changed, it will always result in a re-render of
 * all consumers, regardless of whether or not the updated item is explicitly used in consuming view/component.
 * In other words, it's an all or nothing re-render, so we should be judicious with items we add.
 */
export function GlobalStateProvider({ children }: { children: ReactNode }): JSX.Element {
  const { authToken = '', customerId } = useAuth();

  const [devicesCount, setDevicesCount] = useState<number | null>();
  const [groupsCount, setGroupsCount] = useState<number | null>();
  const [projectsCount, setProjectsCount] = useState<number | null>();
  const [selectedControllerVersion, setSelectedControllerVersion] = useState<ControllerVersion | null>();
  const [showNewControllerTooltip, setShowNewControllerTooltip] = useState<boolean>(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[] | null>(null);

  const [getCounts, counts, countsError] = useGetAccountMetadata(authToken);
  const [getAppPrefs, appPrefs, appPrefsError] = useGetUserPrefs(authToken);
  const [updateAppPrefs, updatedPrefs, updatedPrefsError] = useUpdateUserPrefs(authToken);
  const [getFeatureFlags, featureFlagsResponse, featureFlagsError] = useGetFeatureFlags(authToken);

  //
  // handle selected controller account counts
  //
  const handleFetchCounts = useCallback(() => {
    if (customerId) {
      getCounts(customerId);
    }
  }, [getCounts, customerId]);

  //
  // handle selected controller version pref updates
  //
  const handleUpdateSelectedControllerVersion = useCallback(
    (optInControllerVersion: ControllerVersion) => {
      updateAppPrefs({
        appId: 'controller',
        preferences: { optInControllerVersion },
      });
    },
    [updateAppPrefs]
  );

  //
  // handle app prefs retrieval
  //
  useEffect(() => {
    if (featureFlags) {
      // make sure we have the latest feature flags before checking for controller version
      if (featureFlags.find((flag) => flag.name === 'controller-force-v2')) {
        setSelectedControllerVersion(ControllerVersion.ControllerV2);
      } else if (appPrefsError) {
        setSelectedControllerVersion(ControllerVersion.ControllerV2);
      } else if (appPrefs) {
        setSelectedControllerVersion(
          appPrefs.appId === 'controller' ? appPrefs.preferences.optInControllerVersion || null : null
        );
      }
    }
  }, [appPrefs, appPrefsError, featureFlags]);

  useEffect(() => {
    if (updatedPrefs && !updatedPrefsError) {
      setSelectedControllerVersion(
        updatedPrefs.appId === 'controller' ? updatedPrefs.preferences.optInControllerVersion : null
      );
    } else if (updatedPrefsError) {
      console.log('Unable to update Controller preference', updatedPrefsError);
    }
  }, [updatedPrefs, updatedPrefsError]);

  useEffect(() => {
    if (featureFlagsError) {
      setFeatureFlags([]);
      console.log('Unable to retrieve feature flags:', featureFlagsError);
    } else if (featureFlagsResponse) {
      setFeatureFlags(featureFlagsResponse.featureFlags || []);
    }
  }, [featureFlagsResponse, featureFlagsError]);

  useEffect(() => {
    if (countsError && !counts) {
      setDevicesCount(null);
      setGroupsCount(null);
      setProjectsCount(null);
    }
  }, [counts, countsError]);

  useEffect(() => {
    if (counts?.accountMetadata) {
      setDevicesCount(counts.accountMetadata.devices.total);
      setGroupsCount(counts.accountMetadata.groups.total);
      setProjectsCount(counts.accountMetadata.projects?.total || null);
    }
  }, [counts]);

  useEffect(() => {
    if (customerId) {
      getCounts(customerId);
      getAppPrefs('controller');
      getFeatureFlags({});
    }
  }, [getCounts, customerId, getAppPrefs, getFeatureFlags]);

  return (
    <GlobalStateContext.Provider
      value={{
        devicesCount,
        groupsCount,
        projectsCount,
        selectedControllerVersion,
        showNewControllerTooltip,
        featureFlags: featureFlags || [],
        setShowNewControllerTooltip,
        fetchCounts: handleFetchCounts,
        updateSelectedControllerVersion: handleUpdateSelectedControllerVersion,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
}
